import type { HeroBlockProps } from '../types';
import { theme, sectionColors } from '../theme';

/**
 * Pure presentational hero. Props-only, no hooks/handlers, no "use client".
 *
 * Layout variants (Pick-a-Look): 'classic' (default) is the full-bleed image/
 * video/carousel hero with overlaid text — UNCHANGED, so every existing site
 * renders exactly as before. 'split' / 'minimal' / 'editorial' are contained,
 * surface-themed layouts used by template looks; they use a single image (no
 * video/carousel) and no dark scrim. All server-safe.
 */
export function HeroBlock(props: HeroBlockProps & { previewStatic?: boolean }) {
  switch (props.variant) {
    case 'split':
      return <HeroSplit {...props} />;
    case 'minimal':
      return <HeroMinimal {...props} />;
    case 'editorial':
      return <HeroEditorial {...props} />;
    default:
      return <HeroClassic {...props} />;
  }
}

// ── Classic (default) — full-bleed media + overlaid text. UNCHANGED. ────────────
function HeroClassic(props: HeroBlockProps & { previewStatic?: boolean }) {
  const {
    heading,
    subheading,
    backgroundImageUrl,
    ctaLabel,
    ctaHref,
    alignment = 'center',
    showSearch = false,
    searchPlaceholder,
    mediaType = 'image',
    carouselImages,
    videoUrl,
    videoPosterUrl,
    previewStatic,
  } = props;
  const align =
    alignment === 'left' ? 'items-start text-left'
      : alignment === 'right' ? 'items-end text-right'
        : 'items-center text-center';
  const colors = sectionColors(props, { bg: theme.secondary, text: '#ffffff' });

  // Resolve the media. A carousel needs ≥2 images (else it's just an image); a
  // video needs a URL. Anything else falls back to the single background image.
  const slides = (carouselImages ?? []).map((s) => (s ?? '').trim()).filter(Boolean);
  const useVideo = mediaType === 'video' && !!(videoUrl ?? '').trim();
  const useCarousel = mediaType === 'carousel' && slides.length > 1;
  // Collage: a full-bleed mosaic of the provided images (cycled to fill 6 cells). Needs ≥1 image.
  const useCollage = mediaType === 'collage' && slides.length > 0;
  const collageCells = useCollage ? Array.from({ length: 6 }, (_, i) => slides[i % slides.length]) : [];
  const useMediaLayer = useVideo || useCarousel || useCollage;

  // Image mode keeps the section background-image (back-compat). Media-layer modes
  // paint behind the content and add a dark scrim so the text stays legible.
  const sectionStyle = {
    ...colors,
    ...(useMediaLayer
      ? {}
      : { backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'var(--hero-image, none)' }),
  };

  return (
    <section
      className={`relative flex min-h-[60vh] flex-col justify-center overflow-hidden bg-cover bg-center px-6 py-24 ${align}`}
      style={sectionStyle}
    >
      {useVideo ? (
        previewStatic ? (
          // Builder preview: render a STATIC frame (poster / bg image) instead of an
          // autoplaying <video>, so a large/4K hero video can't freeze the editor.
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{
                backgroundImage: (videoPosterUrl || backgroundImageUrl)
                  ? `url("${videoPosterUrl || backgroundImageUrl}")`
                  : undefined,
              }}
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
            <div className="absolute right-3 top-3 z-20 rounded-full bg-black/60 px-2.5 py-1 text-[11px] font-medium text-white">
              ▶ Video background — plays on the live site
            </div>
          </>
        ) : (
          <>
            <video
              className="absolute inset-0 h-full w-full object-cover"
              autoPlay
              loop
              muted
              playsInline
              preload="metadata"
              poster={videoPosterUrl || undefined}
              aria-hidden="true"
            >
              <source src={(videoUrl ?? '').trim()} type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-black/40" aria-hidden="true" />
          </>
        )
      ) : useCarousel ? (
        <>
          <div className={`blk-hero-carousel-${slides.length} absolute inset-0`} aria-hidden="true">
            {slides.map((src, i) => (
              <div
                key={`${i}-${src}`}
                className="blk-hero-slide absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url("${src}")` }}
              />
            ))}
          </div>
          <div className="absolute inset-0 bg-black/30" aria-hidden="true" />
          <style dangerouslySetInnerHTML={{ __html: carouselCss(slides.length) }} />
        </>
      ) : useCollage ? (
        <>
          {/* Full-bleed image mosaic (server-safe, no JS). 3×2 on desktop, 2×3 on mobile. */}
          <div className="absolute inset-0 grid grid-cols-2 grid-rows-3 gap-0.5 md:grid-cols-3 md:grid-rows-2" aria-hidden="true">
            {collageCells.map((src, i) => (
              <div key={`${i}-${src}`} className="bg-cover bg-center" style={{ backgroundImage: `url("${src}")` }} />
            ))}
          </div>
          <div className="absolute inset-0 bg-black/45" aria-hidden="true" />
        </>
      ) : null}

      {/* Legibility scrim for the overlaid white text. The media-layer modes (video/
          carousel) add their own above; this covers the plain background-image case
          (a bright photo) and adds a gentle lift over solid-color heroes so white text
          stays readable on mid-tone bands (e.g. a teal section), not just dark ones. */}
      {!useMediaLayer ? (
        <div
          className={`pointer-events-none absolute inset-0 z-0 ${backgroundImageUrl ? 'bg-black/40' : 'bg-black/20'}`}
          aria-hidden="true"
        />
      ) : null}

      <div className={`relative z-10 flex w-full flex-col ${align}`}>
        <h1 className="max-w-3xl text-4xl font-semibold drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] md:text-6xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
        {subheading ? <p className="mt-4 max-w-2xl text-lg opacity-90 drop-shadow md:text-xl">{subheading}</p> : null}
        {showSearch ? (
          // Server-safe GET form → the tenant's /listings results page. name="address"
          // → the gateway matches with contains(UnparsedAddress), which spans street +
          // city + state + zip, so the one box searches all of them. Empty submit
          // shows all listings.
          <form action="/listings" method="get" className="mt-8 flex w-full max-w-xl overflow-hidden rounded-md bg-white shadow-lg">
            <input
              type="text"
              name="address"
              placeholder={searchHintOf(searchPlaceholder)}
              className="w-full px-4 py-3 outline-none"
              style={{ color: theme.text }}
            />
            <button type="submit" className="shrink-0 px-6 py-3 font-medium text-white" style={{ backgroundColor: theme.primary }}>
              Search
            </button>
          </form>
        ) : null}
        {ctaLabel && ctaHref ? (
          <a href={ctaHref} className="mt-8 inline-block rounded-md px-6 py-3 font-medium hover:opacity-90" style={{ backgroundColor: theme.accent, color: theme.secondary }}>
            {ctaLabel}
          </a>
        ) : null}
      </div>
    </section>
  );
}

// ── Split — contained two-column: text/search beside a tall image. ──────────────
function HeroSplit(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors}>
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-stretch lg:grid-cols-2">
        <div className="flex flex-col justify-center px-6 py-16 md:px-12 md:py-24">
          <h1 className="text-4xl font-semibold leading-tight md:text-5xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
          {subheading ? <p className="mt-4 max-w-xl text-lg opacity-80">{subheading}</p> : null}
          {showSearch ? <HeroSearchForm searchPlaceholder={searchPlaceholder} className="mt-8 max-w-md" /> : null}
          {ctaLabel && ctaHref ? <HeroCta ctaLabel={ctaLabel} ctaHref={ctaHref} className="mt-8 self-start" /> : null}
        </div>
        <div
          className="relative min-h-[42vh] bg-cover bg-center lg:min-h-[36rem]"
          style={{ backgroundImage: backgroundImageUrl ? `url("${backgroundImageUrl}")` : 'var(--hero-image, none)' }}
          aria-hidden="true"
        />
      </div>
    </section>
  );
}

// ── Minimal — contained, centered, big headline + generous whitespace. ──────────
function HeroMinimal(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors} className="px-6 py-24 md:py-32">
      <div className="mx-auto flex max-w-4xl flex-col items-center text-center">
        <h1 className="max-w-3xl text-5xl font-semibold leading-[1.05] md:text-7xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
        {subheading ? <p className="mt-6 max-w-2xl text-lg opacity-80 md:text-xl">{subheading}</p> : null}
        {showSearch ? <HeroSearchForm searchPlaceholder={searchPlaceholder} className="mt-10 w-full max-w-xl" /> : null}
        {ctaLabel && ctaHref ? <HeroCta ctaLabel={ctaLabel} ctaHref={ctaHref} className="mt-10" /> : null}
      </div>
      {backgroundImageUrl ? (
        <div
          className="mx-auto mt-16 aspect-[16/7] w-full max-w-6xl bg-cover bg-center"
          style={{ backgroundImage: `url("${backgroundImageUrl}")` }}
          aria-hidden="true"
        />
      ) : null}
    </section>
  );
}

// ── Editorial — asymmetric: oversized headline beside an offset portrait image. ──
function HeroEditorial(props: HeroBlockProps) {
  const { heading, subheading, backgroundImageUrl, ctaLabel, ctaHref, showSearch, searchPlaceholder } = props;
  const colors = sectionColors(props, { bg: theme.background, text: theme.text });
  return (
    <section style={colors} className="px-6 py-20 md:py-28">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 md:grid-cols-12 md:items-end">
        <div className="md:col-span-7">
          <h1 className="text-5xl font-semibold leading-[0.95] md:text-8xl" style={{ fontFamily: theme.headingFont }}>{heading}</h1>
          {subheading ? <p className="mt-6 max-w-lg text-lg opacity-80">{subheading}</p> : null}
          {(showSearch || (ctaLabel && ctaHref)) ? (
            <div className="mt-8 flex flex-wrap items-center gap-4">
              {showSearch ? <HeroSearchForm searchPlaceholder={searchPlaceholder} className="w-full max-w-md" /> : null}
              {ctaLabel && ctaHref ? <HeroCta ctaLabel={ctaLabel} ctaHref={ctaHref} /> : null}
            </div>
          ) : null}
        </div>
        <div className="md:col-span-5">
          {backgroundImageUrl ? (
            <div
              className="aspect-[3/4] w-full bg-cover bg-center"
              style={{ backgroundImage: `url("${backgroundImageUrl}")` }}
              aria-hidden="true"
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

// ── Shared bits for the contained variants ──────────────────────────────────────
function HeroSearchForm({ searchPlaceholder, className = '' }: { searchPlaceholder?: string; className?: string }) {
  return (
    <form action="/listings" method="get" className={`flex overflow-hidden rounded-md bg-white shadow-lg ${className}`}>
      <input
        type="text"
        name="address"
        placeholder={searchHintOf(searchPlaceholder)}
        className="w-full px-4 py-3 outline-none"
        style={{ color: theme.text }}
      />
      <button type="submit" className="shrink-0 px-6 py-3 font-medium text-white" style={{ backgroundColor: theme.primary }}>
        Search
      </button>
    </form>
  );
}

function HeroCta({ ctaLabel, ctaHref, className = '' }: { ctaLabel: string; ctaHref: string; className?: string }) {
  return (
    <a
      href={ctaHref}
      className={`inline-block rounded-md px-6 py-3 font-medium text-white hover:opacity-90 ${className}`}
      style={{ backgroundColor: theme.primary }}
    >
      {ctaLabel}
    </a>
  );
}

function searchHintOf(searchPlaceholder?: string): string {
  // Fall back on a sensible placeholder for undefined AND blank '' (the form's
  // default is '', and the AI may omit it) — otherwise the bar shows no hint.
  return searchPlaceholder?.trim() ? searchPlaceholder : 'City, neighborhood, or ZIP…';
}

/**
 * CSS-only cross-fade carousel keyframes for N slides (server-safe, no JS). Each
 * slide is featured ~5s; the N slides cycle in order with a soft cross-fade at the
 * seams. Stacked slides default to opacity:0; the staggered animation reveals one
 * at a time.
 */
function carouselCss(n: number): string {
  const perSlide = 5; // seconds each slide is featured
  const total = n * perSlide;
  const slicePct = 100 / n;
  const fadePct = Math.min(6, slicePct / 3);
  const a = fadePct.toFixed(3);
  const b = Math.max(fadePct, slicePct - fadePct).toFixed(3);
  const c = (slicePct + fadePct).toFixed(3);
  const name = `blkHeroFade${n}`;
  let css =
    `@keyframes ${name}{0%{opacity:0}${a}%{opacity:1}${b}%{opacity:1}${c}%{opacity:0}100%{opacity:0}}` +
    `.blk-hero-carousel-${n} .blk-hero-slide{opacity:0;animation:${name} ${total}s infinite;animation-timing-function:ease-in-out}`;
  for (let i = 0; i < n; i++) {
    css += `.blk-hero-carousel-${n} .blk-hero-slide:nth-child(${i + 1}){animation-delay:${i * perSlide}s}`;
  }
  return css;
}
